import { clamp, type Point, type Rect, type Size } from '../../doc/geometry';

/** Screen offset of the world origin (px) plus zoom. screen = world * zoom + (x, y). */
export interface Camera {
  x: number;
  y: number;
  zoom: number;
}

export const MIN_ZOOM = 0.02;
export const MAX_ZOOM = 16;

export const screenToWorld = (c: Camera, p: Point): Point => ({
  x: (p.x - c.x) / c.zoom,
  y: (p.y - c.y) / c.zoom,
});

export const worldToScreen = (c: Camera, p: Point): Point => ({
  x: p.x * c.zoom + c.x,
  y: p.y * c.zoom + c.y,
});

/** Zoom so that the world point under `screenPt` stays put. */
export function zoomAt(c: Camera, screenPt: Point, newZoom: number): Camera {
  const zoom = clamp(newZoom, MIN_ZOOM, MAX_ZOOM);
  const k = zoom / c.zoom;
  return {
    x: screenPt.x - (screenPt.x - c.x) * k,
    y: screenPt.y - (screenPt.y - c.y) * k,
    zoom,
  };
}

export const panBy = (c: Camera, dx: number, dy: number): Camera => ({
  x: c.x + dx,
  y: c.y + dy,
  zoom: c.zoom,
});

export function visibleWorldRect(c: Camera, size: Size): Rect {
  const tl = screenToWorld(c, { x: 0, y: 0 });
  return { x: tl.x, y: tl.y, w: size.w / c.zoom, h: size.h / c.zoom };
}

export function fitRect(rect: Rect, size: Size, padding = 64, maxZoom = 1): Camera {
  const availW = Math.max(1, size.w - padding * 2);
  const availH = Math.max(1, size.h - padding * 2);
  const zoom = clamp(Math.min(availW / Math.max(rect.w, 1), availH / Math.max(rect.h, 1)), MIN_ZOOM, maxZoom);
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  return { x: size.w / 2 - cx * zoom, y: size.h / 2 - cy * zoom, zoom };
}

export function centerOn(worldPt: Point, size: Size, zoom: number): Camera {
  return { x: size.w / 2 - worldPt.x * zoom, y: size.h / 2 - worldPt.y * zoom, zoom };
}
